import { AxiosError } from 'axios';
import React, { FC, ReactNode, Suspense, useEffect } from 'react';
import { ReflexContainer, ReflexElement, ReflexSplitter } from 'react-reflex';
import { Navigate, Outlet, Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import './App.less';
import { useAppGlobStore } from './appGlobalStore';
import { XmSpinner } from './component/spinner/XmSpinner';
import { ChatIcon } from './content/aiChat/AIChat';
import ErrorBoundary from './content/errorBoundary/ErrorBoundary';
import { ErrorPage, ErrorRoutePage } from './content/errorBoundary/ErrorPage';
import HealthStatusDrawer from './content/globalHealthStatus/HealthStatusDrawer';
import { Login } from './content/login/Login';
import { Logout } from './content/logout/Logout';
import MenuFavoritesDrawer from './content/menuFavorite/MenuFavoritesDrawer';
import { ChangeLogModal } from './content/releaseInfo/ChangeLog';
import { RoutePath } from './data/Routes';
import { useWebSocketGrafana, useWebSocketLLM, useWebSocketReload } from './hook/useWebSocketReload';
import LeftMenuDesktop from "./menu/left/LeftMenuDesktop";
import LeftMenuMobile from './menu/left/Mobile/LeftMenuMobile';
import { reloadPage } from './menu/left/SideNavBar';
import TopNavBar from './menu/top/TopNavBar';
import { RouteClassTypeItem } from './routing/RouteClassTypeItem';
import { RouteCmp } from './routing/RouteCmp';
import { RouteDevice } from './routing/RouteDevice';
import { RouteGroup } from './routing/RouteGroup';
import { RouteSectionCmp } from './routing/RouteSectionCmp';
import { RouteSettingsHelp } from './routing/RouteSettingsHelp';
import { RouteType } from './routing/RouteType';
import { DataService } from './service/data.service';
import { Log } from './service/log.service';
import { DomUtil } from './util/DomUtil';
import { GLOB } from './util/Glob';

export interface AppState {
  /**
   * @deprecated avoid, TODO: replace
   */
  refresh: boolean;
  isHsOpen: boolean;
  isFavoritesOpen: boolean;
}

interface AppStateFull extends AppState {
  entryElement: ReactNode;
  leftTreeWidth: number;
  isFreeEdition: boolean;
  ready: boolean;
}

const KEY_LEFT_TREE_WIDTH = "xm-left-tree-width";
const IS_MOBILE_THRESHOLD = 800;

// required for #1250 - keep focus on modal even when element outside modal tree is clicked so close by ESC works
// document.querySelector('body').addEventListener('DOMActivate', ev =>
//   !(ev.target as HTMLElement).closest('.ant-modal,.ant-picker-dropdown,.ant-input') && setTimeout(() => {
//     document.querySelector<HTMLElement>('.ant-modal-wrap')?.focus();
//   }));

class App extends React.Component<unknown, AppStateFull> {
  private unsubscribeFreeEdition: () => void = () => undefined;
  private unsubscribeLeftTreeWidth: () => void = () => undefined;

  private readonly handleFreeEditionChange = (isFreeEdition: boolean): void => {
    this.setState({ isFreeEdition });
  };

  constructor(props) {
    super(props);
    this.state = {
      refresh: false,
      ready: false,
      isHsOpen: false,
      isFavoritesOpen: false,
      entryElement: null,
      leftTreeWidth: useAppGlobStore.getState().leftTreeWidth,
      isFreeEdition: useAppGlobStore.getState().info?.backend?.free ?? false,
    };
    GLOB.getAppState = () => this.state;
    GLOB.setAppState = this.setState.bind(this);
    this.handleInitialLoad();
  }

  componentDidMount(): void {
    this.unsubscribeFreeEdition = useAppGlobStore.subscribe(
      (state) => state.info?.backend?.free ?? false,
      this.handleFreeEditionChange
    );
    this.unsubscribeLeftTreeWidth = useAppGlobStore.subscribe(state => state.leftTreeWidth, width => this.setState({ leftTreeWidth: width }));
    //void DataService.checkVersionReload();
    GLOB.xormonReady.promise.then(() => {
      useAppGlobStore.setState({ licenseStatus: DataService.detectLicenseColor() });
    }, console.debug);
    window.addEventListener("resize", this.handleResize);
  }

  handleInitialLoad = () => {
    if (window.innerWidth < IS_MOBILE_THRESHOLD) {
      useAppGlobStore.setState({ isMobile: true });
    }
  };

  handleResize = () => {
    // Setting isMobile state
    if (window.innerWidth < IS_MOBILE_THRESHOLD) {
      useAppGlobStore.setState({ isMobile: true });
      useAppGlobStore.getState().closeDesktopLeftTree();
    } else {
      useAppGlobStore.setState({ isMobile: false });
      useAppGlobStore.getState().openDesktopLeftTree();
    }
  };

  componentWillUnmount(): void {
    this.unsubscribeFreeEdition();
    this.unsubscribeLeftTreeWidth();
    window.removeEventListener("resize", this.handleResize);
  }

  render() {
    if (!GLOB.authService.isLoggedIn()) {
      return <Login />;
    }

    const showMenu = useAppGlobStore.getState().showMenu;
    const isMobile = useAppGlobStore.getState().isMobile;

    const isReflexSplitterVisible = (): boolean => {
      if (!isMobile && this.state.leftTreeWidth > 1) return true;
      return false;
    };

    return (
      <Router basename={GLOB.getPublicUrl()}>
        <Hooks getState={() => this.state} setState={this.setState.bind(this)} />
        {isMobile && <LeftMenuMobile />}
        <ReflexContainer orientation="vertical" windowResizeAware>
          <ReflexElement
            className="left_movable_panel"
            minSize={!showMenu || isMobile ? 0.5 : 145}
            size={this.state.leftTreeWidth}
            onStopResize={args => {
              useAppGlobStore.setState({ showMenu: true });
              const width = (args.domElement as Element).clientWidth;
              useAppGlobStore.setState({ leftTreeWidth: width });
              localStorage.setItem(KEY_LEFT_TREE_WIDTH, String(width));
            }}
          >
            <LeftMenuDesktop />
          </ReflexElement>


          {!isMobile && <ReflexSplitter
            style={{
              visibility: isReflexSplitterVisible() ? "visible" : "hidden",
              transition: "none",
              width: isReflexSplitterVisible() ? "4px" : 0
            }}
          />}

          <ReflexElement className='d-flex' propagateDimensions={false} propagateDimensionsRate={10}>
            <TopNavBar />
            <HealthStatusDrawer key={`${this.state.isHsOpen}-state`} />
            <MenuFavoritesDrawer key={`${this.state.isFavoritesOpen}-favorites`} />
            {this.state.ready ? <div id={DomUtil.MAIN_SCROLL_ID} data-testid="main-content" ref={useAppGlobStore.getState().mainScrollRef}>
              <ErrorBoundary>
                <Routes>
                  <Route errorElement={<ErrorRoutePage />}>
                    <Route path="/" element={<Suspense fallback={<XmSpinner />}>{this.state.entryElement}</Suspense>} />
                    <Route path={RoutePath.RELOAD} element={<Outlet />} />
                    <Route path={RoutePath.LOGOUT} element={<Logout />} />
                    <Route path={RoutePath.MATCH_CMP} element={<RouteCmp />} />
                    <Route path={RoutePath.MATCH_SECTION_CMP} element={<RouteSectionCmp />} />
                    <Route path={RoutePath.MATCH_SETTINGS_HELP} >
                      <Route index element={<RouteSettingsHelp match={RoutePath.MATCH_SETTINGS_HELP} />} />
                      <Route path={RoutePath.MATCH_GROUP_ONLY} >
                        <Route index element={<RouteSettingsHelp match={`${RoutePath.MATCH_SETTINGS_HELP}/${RoutePath.MATCH_GROUP_ONLY}`} />} />
                        <Route path={RoutePath.MATCH_ITEM_ONLY} element={<RouteSettingsHelp match={`${RoutePath.MATCH_SETTINGS_HELP}/${RoutePath.MATCH_GROUP_ONLY}/${RoutePath.MATCH_ITEM_ONLY}`} />} />
                      </Route>
                    </Route>
                    <Route path={RoutePath.MATCH_DEVICE} element={<RouteDevice />} />
                    <Route path={RoutePath.MATCH_TYPE} element={<RouteType />} />
                    <Route path={RoutePath.MATCH_GROUP_TOP} >
                      <Route index element={<RouteGroup />} />
                      <Route path={RoutePath.MATCH_ITEM_ONLY} element={<RouteGroup />} />
                    </Route>
                    <Route path={RoutePath.MATCH_CLASS_TYPE_ITEM} element={<RouteClassTypeItem />} />
                    <Route path={RoutePath.MATCH_SPLAT} element={<Suspense fallback={<XmSpinner />}>{this.state.entryElement}</Suspense>} />
                  </Route>
                </Routes>
              </ErrorBoundary>
            </div>
              : <XmSpinner />}
          </ReflexElement>

        </ReflexContainer>
      </Router>
    );
  }
}

const Hooks: FC<{ getState: () => AppStateFull, setState: (state: Partial<AppStateFull>) => void; }> = ({ getState, setState, ...props }) => {
  GLOB.grafanaActive = useWebSocketGrafana();
  const llmActive = useWebSocketLLM();
  const dashReload = useWebSocketReload('dashboard');
  const sessionReload = useWebSocketReload('session');
  const changeLogModalTimestamp = useAppGlobStore(state => state.changeLogModalTimestamp);

  useEffect(() => {
    GLOB.xormonReady.promise.then(() => {
      setState({ ready: true, entryElement: <Navigate to={GLOB.getDefaultPath()} replace /> });
    }, (reason: AxiosError) => setState({ entryElement: <ErrorPage detail={reason} /> }));
    return () => {
      setState({ ready: false, entryElement: null });
    };
  }, []);

  useEffect(() => {
    if (!sessionReload) return;
    if (GLOB.isProdEnv()) {
      Log.warn('Server shutdown. You will be logged out in 5s.');
      setTimeout(() => {
        GLOB.authService.logOut();
      }, 5000);
    }
  }, [sessionReload]);

  useEffect(() => {
    if (!dashReload) return;
    void GLOB.menuService.reloadDashboards().then(() => {
      const warnMsg = 'Dashboards were changed by another user, reloading...';
      if (GLOB.selectedClass === RoutePath.DASHBOARD) {
        Log.warn(warnMsg);
        DataService.reloadLeftMenu?.();
      }
      else if (GLOB.selectedClass === RoutePath.SETTINGS && GLOB.selectedType === RoutePath.CONFIGURATION && GLOB.selectedItem?.path === RoutePath.SET_DASH_MANAGE) {
        Log.warn(warnMsg);
        reloadPage();
      }
    });
  }, [dashReload]);

  return <>
    {getState().entryElement && <ChangeLogModal timestamp={changeLogModalTimestamp} />}
    {llmActive && <ChatIcon />}
    {!llmActive && !GLOB.userInfo?.configuration?.aiShown && <ChatIcon promo={true} />}
  </>;
};

export default App;
